import { useMemo } from 'react';
import { CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TradeLogicTunableManifestV2 } from '@einhaus/project314-types';
import type { EntryOptimizerScanPoint } from '../entryOptimizerUtils';

/**
 * 1-D scan curve: mean profit per entry by parameter value, with the incumbent
 * threshold and the scan's best value marked. Entry counts ride on a second axis
 * so support collapse at tight thresholds is visible alongside the profit curve.
 */
const ScanCurveChart = ({
    bestValue,
    label,
    manifest,
    points,
    tunableId
}: {
    bestValue: number | null;
    label: string;
    manifest: TradeLogicTunableManifestV2 | null;
    points: EntryOptimizerScanPoint[];
    tunableId: string;
}) => {
    const incumbent = useMemo(() => {
        const tunable = manifest?.tunables.find((candidate) => candidate.tunableId === tunableId);

        if (!tunable) return null;

        return Array.isArray(tunable.incumbentValue) ? null : tunable.incumbentValue;
    }, [manifest, tunableId]);

    if (points.length === 0) {
        return <div className="text-sm text-zinc-500">No scan points for {label}.</div>;
    }

    return (
        <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-zinc-200">{label}</div>
                <div className="flex flex-wrap gap-3 text-xs text-zinc-500">
                    <span>
                        <span className="mr-1 inline-block h-2 w-4 rounded bg-cyan-500 align-middle" />
                        Avg profit
                    </span>
                    <span>
                        <span className="mr-1 inline-block h-2 w-4 rounded bg-zinc-600 align-middle" />
                        Entries
                    </span>
                    {incumbent !== null && <span className="text-amber-300">┊ incumbent {incumbent}</span>}
                    {bestValue !== null && <span className="text-green-300">┊ best {bestValue}</span>}
                </div>
            </div>
            <div className="h-72 w-full">
                <ResponsiveContainer height="100%" width="100%">
                    <ComposedChart data={points} margin={{ bottom: 8, left: 8, right: 8, top: 8 }}>
                        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                        <XAxis
                            dataKey="value"
                            label={{ fill: '#71717a', fontSize: 12, position: 'insideBottom', offset: -4, value: 'Parameter value' }}
                            stroke="#71717a"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                            type="number"
                        />
                        <YAxis
                            stroke="#71717a"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                            tickFormatter={(value: number) => value.toFixed(2)}
                            yAxisId="profit"
                        />
                        <YAxis
                            orientation="right"
                            stroke="#52525b"
                            tick={{ fill: '#71717a', fontSize: 12 }}
                            tickFormatter={(value: number) => (value >= 1000 ? `${Math.round(value / 1000)}k` : String(value))}
                            yAxisId="entries"
                        />
                        <Tooltip
                            contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: 6 }}
                            formatter={(value, name) => {
                                if (name === 'Avg profit') return [Number(value).toFixed(4), name];
                                if (name === 'Entries') return [Number(value).toLocaleString(), name];

                                return [String(value), name];
                            }}
                            labelFormatter={(value) => {
                                if (typeof value === 'string') return `Value: ${value}`;
                                if (typeof value === 'number' || typeof value === 'bigint') return `Value: ${value.toString()}`;

                                return 'Value';
                            }}
                            labelStyle={{ color: '#e4e4e7' }}
                        />
                        <Line
                            dataKey="entryCount"
                            dot={false}
                            name="Entries"
                            stroke="#52525b"
                            strokeWidth={1.5}
                            type="monotone"
                            yAxisId="entries"
                        />
                        <Line
                            dataKey="meanProfit"
                            dot={{ r: 3 }}
                            name="Avg profit"
                            stroke="#06b6d4"
                            strokeWidth={2}
                            type="monotone"
                            yAxisId="profit"
                        />
                        {incumbent !== null && <ReferenceLine stroke="#fbbf24" strokeDasharray="4 4" x={incumbent} yAxisId="profit" />}
                        {bestValue !== null && <ReferenceLine stroke="#4ade80" strokeDasharray="4 4" x={bestValue} yAxisId="profit" />}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default ScanCurveChart;
